/**
 * LinkedIn media limits and the checks that read them. One file, so the editor's readiness rules, the publisher
 * and the platform spec cannot drift apart (`shared/__tests__/linkedin-limits.guard.test.ts` pins them).
 *
 * Sources (read 2026-11-03, `li-lms-2026-10`), base
 * https://learn.microsoft.com/en-us/linkedin/marketing/community-management/shares/ :
 * - images-api: JPG, GIF, PNG; under 36,152,320 pixels; GIF up to 250 frames; `altText` up to 4,086 characters.
 * - videos-api: MP4, 3 s to 30 min, 75 KB to 500 MB, 4 MB upload parts.
 * - documents-api: PDF, PPT, PPTX, DOC, DOCX; 100 MB; 300 pages.
 * - posts-api article sample: title under 400, description under 4,086.
 *
 * Plain relative imports only: this file runs in the browser and, through `_shared/`, in Deno edge functions.
 */

export const LINKEDIN_IMAGE_MAX_PIXELS = 36_152_320;
export const LINKEDIN_GIF_MAX_FRAMES = 250;
export const LINKEDIN_ALT_TEXT_MAX_CHARS = 4086;
export const LINKEDIN_ARTICLE_TITLE_MAX_CHARS = 400;
export const LINKEDIN_ARTICLE_DESCRIPTION_MAX_CHARS = 4086;
export const LINKEDIN_MEDIA_TITLE_MAX_CHARS = 400;

export const LINKEDIN_VIDEO_MIN_SECONDS = 3;
export const LINKEDIN_VIDEO_MAX_SECONDS = 30 * 60;
export const LINKEDIN_VIDEO_MIN_BYTES = 75 * 1024;
export const LINKEDIN_VIDEO_MAX_BYTES = 500 * 1024 * 1024;
export const LINKEDIN_VIDEO_PART_BYTES = 4 * 1024 * 1024;

export const LINKEDIN_DOCUMENT_MAX_BYTES = 100 * 1024 * 1024;
export const LINKEDIN_DOCUMENT_MAX_PAGES = 300;
export const LINKEDIN_DOCUMENT_EXTENSIONS = ['pdf', 'ppt', 'pptx', 'doc', 'docx'] as const;

export const LINKEDIN_MULTI_IMAGE_MIN = 2;
export const LINKEDIN_MULTI_IMAGE_MAX = 20;

/**
 * The Posts API accepts JPG, GIF and PNG. LinkedIn's own app also takes WebP, and the API page does not say it
 * refuses it, but nobody has proven it. Flip to true only after a live upload of a WebP image succeeds
 * (the run note for 2026-11-04 lists the test).
 */
export const LINKEDIN_API_ACCEPTS_WEBP = false;

const SENDABLE_IMAGE_URL = /\.(jpe?g|png|gif)(?:[?#].*)?$/i;
const ANY_IMAGE_EXTENSION = /\.([a-z0-9]{2,5})(?:[?#].*)?$/i;
const VIDEO_URL = /\.(mp4|mov|webm|m4v)(?:[?#].*)?$/i;
const DOCUMENT_URL = new RegExp(`\\.(${LINKEDIN_DOCUMENT_EXTENSIONS.join('|')})(?:[?#].*)?$`, 'i');

export function isLinkedInDocumentUrl(url: string): boolean {
  return DOCUMENT_URL.test(url);
}

export function isLinkedInDocumentMime(mime: string | null | undefined): boolean {
  if (!mime) return false;
  return (
    mime === 'application/pdf' ||
    mime === 'application/vnd.ms-powerpoint' ||
    mime === 'application/vnd.openxmlformats-officedocument.presentationml.presentation' ||
    mime === 'application/msword' ||
    mime === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
  );
}

/**
 * True when the URL names an image format the LinkedIn API takes, or names no format at all (the publisher reads
 * the real bytes then). False only for a visible extension Mora cannot send or convert, such as `.heic`.
 */
export function isLinkedInSupportedImageUrl(url: string): boolean {
  if (SENDABLE_IMAGE_URL.test(url)) return true;
  const ext = ANY_IMAGE_EXTENSION.exec(url.split(/[?#]/)[0])?.[1]?.toLowerCase();
  if (!ext) return true;
  // The dispatcher converts a WebP to JPEG before the publisher runs, so a WebP is fine to post.
  if (ext === 'webp') return true;
  // Not an image extension at all (a path segment that happens to contain a dot): the bytes decide.
  return !KNOWN_OTHER_IMAGE_EXTENSIONS.has(ext);
}

const KNOWN_OTHER_IMAGE_EXTENSIONS = new Set([
  'webp',
  'heic',
  'heif',
  'avif',
  'bmp',
  'tif',
  'tiff',
  'svg',
  'ico',
]);

export function isLinkedInVideoUrl(url: string): boolean {
  return VIDEO_URL.test(url);
}

export type LinkedInImageFormat = 'jpeg' | 'png' | 'gif' | 'webp' | 'tiff' | 'unknown';

export function sniffImageFormat(bytes: Uint8Array): LinkedInImageFormat {
  if (bytes.length >= 3 && bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff)
    return 'jpeg';
  if (
    bytes.length >= 8 &&
    bytes[0] === 0x89 &&
    bytes[1] === 0x50 &&
    bytes[2] === 0x4e &&
    bytes[3] === 0x47
  ) {
    return 'png';
  }
  if (bytes.length >= 6 && bytes[0] === 0x47 && bytes[1] === 0x49 && bytes[2] === 0x46)
    return 'gif';
  if (
    bytes.length >= 12 &&
    bytes[0] === 0x52 &&
    bytes[1] === 0x49 &&
    bytes[2] === 0x46 &&
    bytes[3] === 0x46 &&
    bytes[8] === 0x57 &&
    bytes[9] === 0x45 &&
    bytes[10] === 0x42 &&
    bytes[11] === 0x50
  ) {
    return 'webp';
  }
  if (
    bytes.length >= 4 &&
    ((bytes[0] === 0x49 && bytes[1] === 0x49 && bytes[2] === 0x2a && bytes[3] === 0x00) ||
      (bytes[0] === 0x4d && bytes[1] === 0x4d && bytes[2] === 0x00 && bytes[3] === 0x2a))
  ) {
    return 'tiff';
  }
  return 'unknown';
}

export interface ImageSize {
  width: number;
  height: number;
}

function jpegSize(bytes: Uint8Array): ImageSize | null {
  let i = 2;
  while (i + 9 < bytes.length) {
    if (bytes[i] !== 0xff) {
      i += 1;
      continue;
    }
    const marker = bytes[i + 1];
    if (marker === 0xff) {
      i += 1;
      continue;
    }
    // Markers with no length: SOI, EOI, RSTn, TEM.
    if (
      marker === 0xd8 ||
      marker === 0xd9 ||
      marker === 0x01 ||
      (marker >= 0xd0 && marker <= 0xd7)
    ) {
      i += 2;
      continue;
    }
    const length = (bytes[i + 2] << 8) | bytes[i + 3];
    const isStartOfFrame =
      marker >= 0xc0 && marker <= 0xcf && marker !== 0xc4 && marker !== 0xc8 && marker !== 0xcc;
    if (isStartOfFrame) {
      const height = (bytes[i + 5] << 8) | bytes[i + 6];
      const width = (bytes[i + 7] << 8) | bytes[i + 8];
      return width > 0 && height > 0 ? { width, height } : null;
    }
    if (length < 2) return null;
    i += 2 + length;
  }
  return null;
}

function pngSize(bytes: Uint8Array): ImageSize | null {
  if (bytes.length < 24) return null;
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const width = view.getUint32(16);
  const height = view.getUint32(20);
  return width > 0 && height > 0 ? { width, height } : null;
}

function gifSize(bytes: Uint8Array): ImageSize | null {
  if (bytes.length < 10) return null;
  const width = bytes[6] | (bytes[7] << 8);
  const height = bytes[8] | (bytes[9] << 8);
  return width > 0 && height > 0 ? { width, height } : null;
}

export function readImageSize(bytes: Uint8Array, format: LinkedInImageFormat): ImageSize | null {
  if (format === 'jpeg') return jpegSize(bytes);
  if (format === 'png') return pngSize(bytes);
  if (format === 'gif') return gifSize(bytes);
  return null;
}

/** Walks the GIF block structure and counts image frames. Returns null when the stream is malformed. */
export function countGifFrames(bytes: Uint8Array): number | null {
  if (bytes.length < 13) return null;
  let i = 13;
  if (bytes[10] & 0x80) i += 3 * (1 << ((bytes[10] & 0x07) + 1));
  let frames = 0;
  const skipSubBlocks = (): boolean => {
    while (i < bytes.length) {
      const size = bytes[i];
      i += 1;
      if (size === 0) return true;
      i += size;
    }
    return false;
  };
  while (i < bytes.length) {
    const block = bytes[i];
    i += 1;
    if (block === 0x3b) return frames;
    if (block === 0x21) {
      i += 1;
      if (!skipSubBlocks()) return null;
    } else if (block === 0x2c) {
      if (i + 9 > bytes.length) return null;
      const packed = bytes[i + 8];
      i += 9;
      if (packed & 0x80) i += 3 * (1 << ((packed & 0x07) + 1));
      i += 1;
      frames += 1;
      if (!skipSubBlocks()) return null;
    } else {
      return null;
    }
  }
  return frames > 0 ? frames : null;
}

export type LinkedInImagePlan =
  | { action: 'send'; format: 'jpeg' | 'png' | 'gif'; size: ImageSize | null; frames?: number }
  | { action: 'reject'; message: string };

export const LINKEDIN_IMAGE_FORMAT_MESSAGE =
  'LinkedIn takes JPG, PNG and GIF images. Save this image as a JPG or PNG and add it again.';

/**
 * What to do with downloaded image bytes before they go to LinkedIn: send, or refuse with a plain sentence. Pure, so
 * it is unit-tested without a network. Scaling an oversize still image is not done here: the dispatcher's
 * `normalizeImageForPlatform` (`shared/image-normalization-rules.ts`) already brings every still image under
 * LinkedIn's pixel limit before the publisher is called, so this is the check that remains for what it does not
 * cover: GIF frames, formats the API does not take, and an image that arrives some other way (a link card's picture).
 */
export function planLinkedInImage(bytes: Uint8Array): LinkedInImagePlan {
  const format = sniffImageFormat(bytes);

  if (format === 'jpeg' || format === 'png') {
    const size = readImageSize(bytes, format);
    if (size && size.width * size.height >= LINKEDIN_IMAGE_MAX_PIXELS) {
      return {
        action: 'reject',
        message:
          'This image is larger than LinkedIn accepts (over 36 million pixels). Use a smaller image.',
      };
    }
    return { action: 'send', format, size };
  }

  if (format === 'gif') {
    const size = readImageSize(bytes, 'gif');
    const frames = countGifFrames(bytes) ?? undefined;
    if (frames !== undefined && frames > LINKEDIN_GIF_MAX_FRAMES) {
      return {
        action: 'reject',
        message: `This GIF has ${frames} frames and LinkedIn takes up to ${LINKEDIN_GIF_MAX_FRAMES}. Shorten the GIF or use a video.`,
      };
    }
    if (size && size.width * size.height >= LINKEDIN_IMAGE_MAX_PIXELS) {
      return {
        action: 'reject',
        message:
          'This GIF is larger than LinkedIn accepts (over 36 million pixels). Use a smaller GIF.',
      };
    }
    return { action: 'send', format, size, frames };
  }

  if (format === 'webp' && LINKEDIN_API_ACCEPTS_WEBP) {
    // Only reachable once the live test passes; the publisher then sends the bytes untouched.
    return { action: 'send', format: 'jpeg', size: null };
  }

  return { action: 'reject', message: LINKEDIN_IMAGE_FORMAT_MESSAGE };
}

export function clampLinkedInAltText(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  return trimmed.length > LINKEDIN_ALT_TEXT_MAX_CHARS
    ? trimmed.slice(0, LINKEDIN_ALT_TEXT_MAX_CHARS)
    : trimmed;
}

/**
 * Alt text from the dispatcher body: an array aligned with `mediaUrls` (the cross-platform contract, `null` where a
 * child has none). Each entry is trimmed and capped at 4,086 characters; anything else yields no alt text.
 */
export function normalizeLinkedInAltTexts(
  raw: unknown,
  mediaUrls: readonly string[],
): (string | undefined)[] {
  const list = Array.isArray(raw) ? raw : [];
  return mediaUrls.map((_, index) => clampLinkedInAltText(list[index]));
}

/**
 * A real title for an image or document, instead of the literal "Image": the post's own title if it has one,
 * else the image's alt text, else the first line of the caption, else the fallback.
 */
export function deriveLinkedInMediaTitle(input: {
  title?: unknown;
  altText?: string;
  commentary?: unknown;
  fallback: string;
}): string {
  const clip = (value: string) => {
    const text = value.replace(/\s+/g, ' ').trim();
    if (text.length <= 100) return text;
    return `${text.slice(0, 99).trimEnd()}…`;
  };
  if (typeof input.title === 'string' && input.title.trim()) return clip(input.title);
  if (input.altText) return clip(input.altText);
  if (typeof input.commentary === 'string') {
    const firstLine = input.commentary.split('\n').find((line) => line.trim().length > 0);
    if (firstLine) return clip(firstLine);
  }
  return input.fallback;
}

const FIRST_URL = /https?:\/\/[^\s<>()[\]{}"'`]+/i;
const URL_TAIL_PUNCTUATION = /[.,;:!?]+$/;

/**
 * The first http(s) link in a caption, trimmed of sentence punctuation. Same rule as the dispatcher's
 * `extractFirstUrl` (`_shared/utm-stamper.ts`), so the editor previews the link the publisher will use.
 */
export function extractFirstHttpUrl(text: unknown): string | null {
  if (typeof text !== 'string' || text.length === 0) return null;
  const match = text.match(FIRST_URL);
  if (!match) return null;
  const url = match[0].replace(URL_TAIL_PUNCTUATION, '');
  return url.length > 0 ? url : null;
}

/** LinkedIn takes an article title under 400 characters and a description under 4,086. */
export function clampLinkedInArticleText(
  value: string | undefined,
  limit: number,
): string | undefined {
  if (!value) return undefined;
  const text = value.trim();
  if (!text) return undefined;
  const max = limit - 1;
  return text.length <= max ? text : `${text.slice(0, max - 1).trimEnd()}…`;
}

/**
 * The file name LinkedIn shows as the document's title: the stored name without Mora's upload timestamp prefix
 * (`1730000000000-Q3_report.pdf` becomes `Q3_report.pdf`).
 */
export function linkedInDocumentTitleFromUrl(url: string): string {
  const path = url.split(/[?#]/)[0];
  const last = path.split('/').pop() ?? '';
  let name = last;
  try {
    name = decodeURIComponent(last);
  } catch {
    name = last;
  }
  name = name.replace(/^\d{10,}-/, '').trim();
  const clipped =
    name.length > LINKEDIN_MEDIA_TITLE_MAX_CHARS
      ? name.slice(0, LINKEDIN_MEDIA_TITLE_MAX_CHARS)
      : name;
  return clipped || 'Document';
}

/**
 * A best-effort page count for a PDF, read from the page tree's `/Count` entries and the page objects themselves.
 * Returns null when the file hides its page tree inside compressed object streams (the count is then unknown, and
 * the check lets LinkedIn decide).
 */
export function estimatePdfPageCount(bytes: Uint8Array): number | null {
  if (bytes.length < 8 || String.fromCharCode(...bytes.slice(0, 4)) !== '%PDF') return null;
  let text = '';
  const chunk = 1 << 20;
  for (let i = 0; i < bytes.length; i += chunk) {
    text += new TextDecoder('latin1').decode(bytes.subarray(i, Math.min(i + chunk, bytes.length)));
  }
  let fromCounts = 0;
  for (const match of text.matchAll(
    /\/Type\s*\/Pages\b[^>]*?\/Count\s+(\d+)|\/Count\s+(\d+)[^>]*?\/Type\s*\/Pages\b/g,
  )) {
    fromCounts = Math.max(fromCounts, Number(match[1] ?? match[2]));
  }
  const fromObjects = (text.match(/\/Type\s*\/Page(?![a-zA-Z])/g) ?? []).length;
  const best = Math.max(fromCounts, fromObjects);
  return best > 0 ? best : null;
}

export interface LinkedInDocumentCheck {
  name: string;
  sizeBytes: number;
  pageCount?: number | null;
}

/** A plain sentence when a document cannot go to LinkedIn, or null when it can. */
export function linkedInDocumentProblem(doc: LinkedInDocumentCheck): string | null {
  if (!isLinkedInDocumentUrl(doc.name)) {
    return 'LinkedIn takes PDF, PowerPoint (PPT, PPTX) and Word (DOC, DOCX) documents.';
  }
  if (doc.sizeBytes > LINKEDIN_DOCUMENT_MAX_BYTES) {
    return 'This document is larger than 100 MB, which is the most LinkedIn takes. Compress it and try again.';
  }
  if (doc.sizeBytes <= 0) return 'This document is empty.';
  if (typeof doc.pageCount === 'number' && doc.pageCount > LINKEDIN_DOCUMENT_MAX_PAGES) {
    return `This document has ${doc.pageCount} pages and LinkedIn takes up to ${LINKEDIN_DOCUMENT_MAX_PAGES}. Split it and try again.`;
  }
  return null;
}
